import useStores from 'shared/hooks/use-stores';
import { useObserver } from 'mobx-react-lite';
import { dollarsToCents, formatCurrency } from 'shared/helpers/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { useCheckoutLineItems } from 'src/checkout/hooks/use-checkout-line-items';
import { GetUserIncentiveEligibilityDocument } from 'src/gql/graphql';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { ValidIncentiveValue, isValidIncentiveValue } from '../shared/incentive-types';

// Static minimum for copy when eligibility provides none: eligibility is only fetched for
// logged-in users behind the rollout flag, so guests and the anonymous kiosk never get a value.
const DEFAULT_CASH_BACK_MINIMUM_CENTS = 4000;

export type UseCashBackIncentivesOptions = {
  // Optional Checkout attribution owned by the explicit context (e.g. the viewed order). Never falls back to the
  // active cart's token when explicit options are supplied.
  checkoutToken?: string | null;
  dispensaryId?: string;
  enabled?: boolean;
  failClosed?: boolean;
  totalAmount?: number;
};

export type CashBackIncentiveData = {
  eligible: boolean;
  incentive?: {
    id: string;
    name: string;
    description?: string | null;
    amountCents: number;
    cohortName: string;
    active: boolean;
    minimumTotalAmountCents?: number | null;
  } | null;
};

// Tokens are opaque; only presence matters. Absent, null, non-string, empty, and whitespace-only values are unusable.
function getUsableToken(token: unknown): string | undefined {
  return typeof token === 'string' && token.trim() !== '' ? token : undefined;
}

export function useCashBackIncentives(
  options?: UseCashBackIncentivesOptions
): {
  incentiveData: CashBackIncentiveData | null;
  loading: boolean;
  error: any;
  shouldShowBanner: boolean;
  cashBackIncentiveValue: ValidIncentiveValue | undefined;
  cashBackMinimumOrderTotalDisplay: string;
} {
  const { Cart, User } = useStores();
  const flags = useFlags();
  // Explicit context supplies its own total, so it must not subscribe to the active cart's totals.
  const checkoutLineItems = useCheckoutLineItems({ enabled: options === undefined });

  const cashBackRedemptionEnabled = flags['fintech.incentives.redemption.fe.rollout'] ?? false;
  const cartTotal = checkoutLineItems.find((lineItem) => lineItem.type === 'total')?.value;
  const requestEnabled = options?.enabled ?? true;
  const failClosed = options?.failClosed ?? false;
  const dispensaryId = options === undefined ? Cart.order.dispensary?.id : options.dispensaryId;
  const totalAmount = options === undefined ? cartTotal : options.totalAmount;
  const totalAmountCents =
    totalAmount !== undefined && Number.isFinite(totalAmount) && totalAmount > 0
      ? dollarsToCents(totalAmount, true)
      : undefined;

  // Default (no options) uses the active cart's Checkout token; explicit context supplies its own or none. Read inside
  // the observer only for the default path so unrelated cart changes never re-render or reattribute explicit context.
  const contextCheckoutToken = useObserver(() => (options === undefined ? Cart.checkoutToken : options.checkoutToken));
  const checkoutToken = getUsableToken(contextCheckoutToken);

  const canRequestEligibility =
    requestEnabled && cashBackRedemptionEnabled && Boolean(User.exists) && Boolean(User.id) && Boolean(dispensaryId);

  // The existing DPay browsing-session token owner mints (once, via sessionStorage) and reuses the token. It is only
  // asked to mint when an eligibility request could otherwise be issued, so unrelated surfaces never mint.
  const { eventToken, isSessionTokenLoading } = useDutchiePayAnalytics({ mintSessionToken: canRequestEligibility });
  const dutchiePaySessionToken = getUsableToken(eventToken);

  const shouldSkip = !canRequestEligibility || dutchiePaySessionToken === undefined;
  // While the mint is genuinely in flight for an otherwise-requestable request, the offer is still resolving.
  // Mint errors and unusable minted tokens settle (isSessionTokenLoading is false), so this never sticks.
  const isAwaitingSessionToken = canRequestEligibility && dutchiePaySessionToken === undefined && isSessionTokenLoading;

  const { data, loading, error } = useQuery(GetUserIncentiveEligibilityDocument, {
    skip: shouldSkip,
    variables: {
      externalId: User.id,
      dispensaryId,
      ...(totalAmountCents !== undefined && { totalAmountCents }),
      dutchiePaySessionToken,
      ...(checkoutToken !== undefined && { checkoutToken }),
    },
    fetchPolicy: failClosed ? 'network-only' : 'cache-first',
    errorPolicy: failClosed ? 'all' : 'ignore',
  });

  const hasUsableResponse = !shouldSkip && (!failClosed || (!loading && !error));
  const incentiveData = hasUsableResponse ? data?.getUserIncentiveEligibility ?? null : null;
  const amountCents = incentiveData?.incentive?.amountCents;
  const calculatedValue =
    amountCents !== undefined && (!failClosed || amountCents % 100 === 0) ? Math.round(amountCents / 100) : undefined;
  const cashBackIncentiveValue = isValidIncentiveValue(calculatedValue) ? calculatedValue : undefined;
  const hasActiveIncentive = !failClosed || incentiveData?.incentive?.active === true;

  const shouldShowBanner = Boolean(
    incentiveData?.eligible && hasActiveIncentive && cashBackIncentiveValue !== undefined
  );

  // DutchiePay validates the minimum as >= 1 (an invalid experiment value or LD override falls
  // back to the persisted positive minimum), so a 0 here means malformed data, not "no minimum".
  const minimumCents = incentiveData?.incentive?.minimumTotalAmountCents;
  const effectiveMinimumCents =
    minimumCents != null && minimumCents > 0 ? minimumCents : DEFAULT_CASH_BACK_MINIMUM_CENTS;

  return {
    incentiveData,
    loading: loading || isAwaitingSessionToken,
    error,
    shouldShowBanner,
    cashBackIncentiveValue,
    // Round up so the copy never understates the server-enforced minimum (a shopper at exactly the
    // shown amount must qualify).
    cashBackMinimumOrderTotalDisplay: formatCurrency(Math.ceil(effectiveMinimumCents / 100), { trimCents: true }),
  };
}
